import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, History } from "lucide-react";
import { api, apiError, fmtEUR, fmtDate } from "../lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const EMPTY = {
    name: "", description: "", price_eur: "", currency: "EUR", periodicity: "mensuel", features: "",
    published: false,
    promo: { active: false, promo_price_eur: "", percent: "", starts_at: "", ends_at: "", conditions: "" },
};

const toPayload = (f) => ({
    name: f.name,
    description: f.description,
    price_cents: Math.round(parseFloat(String(f.price_eur).replace(",", ".")) * 100) || 0,
    currency: f.currency,
    periodicity: f.periodicity,
    features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
    published: f.published,
    promo: {
        active: f.promo.active,
        promo_price_cents: f.promo.promo_price_eur ? Math.round(parseFloat(String(f.promo.promo_price_eur).replace(",", ".")) * 100) : null,
        percent: f.promo.percent ? parseInt(f.promo.percent) : null,
        starts_at: f.promo.starts_at ? new Date(f.promo.starts_at).toISOString() : null,
        ends_at: f.promo.ends_at ? new Date(f.promo.ends_at).toISOString() : null,
        conditions: f.promo.conditions,
    },
});

const fromOffer = (o) => ({
    name: o.name, description: o.description, price_eur: (o.price_cents / 100).toFixed(2).replace(".", ","),
    currency: o.currency, periodicity: o.periodicity, features: (o.features || []).join("\n"), published: o.published,
    promo: {
        active: o.promo?.active || false,
        promo_price_eur: o.promo?.promo_price_cents != null ? (o.promo.promo_price_cents / 100).toFixed(2).replace(".", ",") : "",
        percent: o.promo?.percent || "",
        starts_at: o.promo?.starts_at ? o.promo.starts_at.slice(0, 16) : "",
        ends_at: o.promo?.ends_at ? o.promo.ends_at.slice(0, 16) : "",
        conditions: o.promo?.conditions || "",
    },
});

export default function Offers() {
    const [offers, setOffers] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [history, setHistory] = useState(null);

    const load = () => api.get("/admin/offers").then((r) => setOffers(r.data)).catch((e) => toast.error(apiError(e)));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = toPayload(form);
            if (editing.offer_id) await api.patch(`/admin/offers/${editing.offer_id}`, payload);
            else await api.post("/admin/offers", payload);
            toast.success("Offre enregistrée.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(apiError(e));
        }
    };

    return (
        <div data-testid="admin-offers">
            <PageHeader title="Tarifs & promotions" subtitle="Le site reflète les offres réellement applicables dans les stores. Une promotion publiée ici n'active aucune remise sur Google Play ou l'App Store.">
                <Button onClick={() => { setForm(EMPTY); setEditing({}); }} className="bg-forest text-cream gap-2" data-testid="offer-add-button"><Plus size={16} /> Nouvelle offre</Button>
            </PageHeader>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {(offers || []).map((o) => (
                    <article key={o.offer_id} className="rounded-2xl bg-white border border-gold/25 p-6 shadow-sm" data-testid={`offer-row-${o.offer_id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="font-heading font-bold text-forest text-lg">{o.name}</h3>
                                <p className="text-xs text-pineneedle">{o.periodicity} · {o.currency}</p>
                            </div>
                            <div className="flex gap-2">
                                <span className={`text-xs px-2 py-1 rounded-full ${o.published ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-700"}`}>{o.published ? "Publiée" : "Masquée"}</span>
                                {o.promo?.active && (
                                    <span className={`text-xs px-2 py-1 rounded-full ${o.promo_live ? "bg-gold/20 text-forest" : "bg-gray-200 text-gray-700"}`} data-testid={`promo-state-${o.offer_id}`}>
                                        Promo {o.promo_live ? "en cours" : "programmée/expirée"}
                                    </span>
                                )}
                            </div>
                        </div>
                        <p className="mt-3 text-2xl font-heading font-extrabold text-forest">
                            {fmtEUR(o.price_cents, o.currency)}
                            {o.promo?.active && o.promo.promo_price_cents != null && (
                                <span className="text-base text-gold font-bold ml-3">→ {fmtEUR(o.promo.promo_price_cents, o.currency)} (-{o.promo.percent || "?"}%)</span>
                            )}
                        </p>
                        <ul className="mt-3 text-xs text-pineneedle space-y-1">{(o.features || []).map((f, i) => <li key={i}>✦ {f}</li>)}</ul>
                        <div className="flex gap-2 mt-5">
                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => { setForm(fromOffer(o)); setEditing(o); }} data-testid={`offer-edit-${o.offer_id}`}><Pencil size={13} /> Modifier</Button>
                            <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => setHistory(o)} data-testid={`offer-history-${o.offer_id}`}><History size={13} /> Historique ({(o.revisions || []).length})</Button>
                            <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-destructive border-destructive/40" onClick={() => setDeleteTarget(o)} data-testid={`offer-delete-${o.offer_id}`}><Trash2 size={13} /></Button>
                        </div>
                    </article>
                ))}
            </div>
            {offers && offers.length === 0 && <p className="rounded-2xl border-2 border-dashed border-gold/40 bg-white p-10 text-center text-pineneedle">Aucune offre. Créez la première.</p>}

            <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
                <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="offer-editor">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-forest">{editing?.offer_id ? "Modifier l'offre" : "Nouvelle offre"}</DialogTitle>
                        <DialogDescription className="text-xs">Ne publiez que des prix conformes aux offres réellement applicables dans les stores.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="offer-name" /></div>
                            <div>
                                <Label>Périodicité</Label>
                                <Select value={form.periodicity} onValueChange={(v) => setForm({ ...form, periodicity: v })}>
                                    <SelectTrigger data-testid="offer-periodicity"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="mensuel">Mensuel</SelectItem>
                                        <SelectItem value="annuel">Annuel</SelectItem>
                                        <SelectItem value="unique">Paiement unique</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="offer-description" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Prix officiel TTC (€)</Label><Input value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} placeholder="29,99" data-testid="offer-price" /></div>
                            <div><Label>Devise</Label><Input value={form.currency} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} data-testid="offer-currency" /></div>
                        </div>
                        <div><Label>Avantages inclus (un par ligne)</Label><Textarea rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="offer-features" /></div>
                        <div className="rounded-xl border border-gold/30 p-4 space-y-4">
                            <label className="flex items-center gap-3 text-sm font-semibold text-forest">
                                <Switch checked={form.promo.active} onCheckedChange={(v) => setForm({ ...form, promo: { ...form.promo, active: v } })} data-testid="promo-active-switch" /> Promotion active
                            </label>
                            {form.promo.active && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><Label>Prix promo TTC (€)</Label><Input value={form.promo.promo_price_eur} onChange={(e) => setForm({ ...form, promo: { ...form.promo, promo_price_eur: e.target.value } })} placeholder="17,99" data-testid="promo-price" /></div>
                                        <div><Label>Réduction (%)</Label><Input type="number" min="1" max="99" value={form.promo.percent} onChange={(e) => setForm({ ...form, promo: { ...form.promo, percent: e.target.value } })} placeholder="40" data-testid="promo-percent" /></div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><Label>Début (facultatif)</Label><Input type="datetime-local" value={form.promo.starts_at} onChange={(e) => setForm({ ...form, promo: { ...form.promo, starts_at: e.target.value } })} data-testid="promo-start" /></div>
                                        <div><Label>Fin (facultatif)</Label><Input type="datetime-local" value={form.promo.ends_at} onChange={(e) => setForm({ ...form, promo: { ...form.promo, ends_at: e.target.value } })} data-testid="promo-end" /></div>
                                    </div>
                                    <div><Label>Conditions applicables</Label><Textarea rows={2} value={form.promo.conditions} onChange={(e) => setForm({ ...form, promo: { ...form.promo, conditions: e.target.value } })} data-testid="promo-conditions" /></div>
                                </>
                            )}
                        </div>
                        <label className="flex items-center gap-3 text-sm text-pineneedle"><Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="offer-published-switch" /> Offre publiée sur le site</label>
                        <Button onClick={save} className="w-full bg-forest text-cream" data-testid="offer-save">Enregistrer</Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!history} onOpenChange={() => setHistory(null)}>
                <DialogContent className="bg-cream max-w-lg" data-testid="offer-history-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-forest">Historique — {history?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {(history?.revisions || []).length === 0 && <p className="text-sm text-pineneedle">Aucune modification enregistrée.</p>}
                        {(history?.revisions || []).slice().reverse().map((r, i) => (
                            <div key={i} className="text-xs bg-white border rounded-lg p-3">
                                <p className="text-pineneedle">{fmtDate(r.at)} par {r.by}</p>
                                <p className="text-forest mt-1">Prix : {fmtEUR(r.data.price_cents, r.data.currency)} · Promo : {r.data.promo?.active ? `${fmtEUR(r.data.promo.promo_price_cents, r.data.currency)}` : "non"} · Publiée : {r.data.published ? "oui" : "non"}</p>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
                <AlertDialogContent className="bg-cream">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer « {deleteTarget?.name} » ?</AlertDialogTitle>
                        <AlertDialogDescription>L'offre disparaîtra immédiatement du site public.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-white" onClick={async () => { try { await api.delete(`/admin/offers/${deleteTarget.offer_id}`); toast.success("Offre supprimée."); load(); } catch (e) { toast.error(apiError(e)); } }} data-testid="offer-delete-confirm">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
